import { Toast } from "@/components/ui";
import { CROSSFADE, SPRINGS } from "@/lib/spring";
import { useToastStore } from "@/stores/toastStore";
// `m` 은 기능이 빠진 껍데기 컴포넌트예요 — 실제 애니메이션 코드는 `main.tsx` 의
// `LazyMotion` 이 첫 페인트 뒤에 받아 옵니다(`lib/motion-features.ts` 에 이유).
import { AnimatePresence, useReducedMotion } from "motion/react";
// 요소별 named export(`div`·`nav`…)라 네임스페이스로 받아요.
import * as m from "motion/react-m";
import { useEffect } from "react";

function ToastRow({
  id,
  message,
  icon,
  duration,
}: ReturnType<typeof useToastStore.getState>["toasts"][number]) {
  const dismiss = useToastStore((s) => s.dismiss);
  const reduce = useReducedMotion();

  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(id), duration);
    return () => window.clearTimeout(timer);
  }, [id, duration, dismiss]);

  /*
    들어온 길로 나가요. 토스트는 화면 아래에 살기 때문에 아래에서 올라오고
    아래로 내려갑니다 — 아래에서 올라와 옆으로 사라지면 어디로 간 건지 알 수
    없고, 다음 토스트가 같은 자리에 오는 이유도 설명되지 않아요.

    스프링을 쓰는 이유는 길이가 아니라 **중단 가능성**입니다. 토스트는 여러 개가
    겹쳐 쌓이고 같은 문구가 오면 스토어가 맨 뒤로 옮겨요(`stores/toastStore.ts`) —
    그때 목표만 바뀌고 진행 중인 값에서 이어지니까 줄이 튀지 않습니다. 고정 길이
    트랜지션은 그 순간 처음부터 다시 시작해서 눈에 걸려요.

    `layout` 은 안 씁니다 — FLIP 은 `domMax` 기능 묶음에만 있고, 그걸 받으면
    최초 청크가 다시 두 배가 돼요. 토스트가 쌓이고 빠질 때 아래 줄이 곧바로
    올라오는 대신 툭 올라오는데, 그 한 프레임이 42KB 값은 아닙니다.

    시트 스프링(damping 0.8)을 쓰는 건 이게 "아래에서 밀려 올라오는" 물건이라
    운동량이 실린 것처럼 읽혀야 하기 때문이에요. 그냥 나타나는 메뉴라면 튐 없는
    쪽이 맞습니다.
  */
  const hidden = reduce ? { opacity: 0 } : { opacity: 0, y: 20, scale: 0.96 };

  return (
    <m.div
      initial={hidden}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={hidden}
      transition={reduce ? CROSSFADE : SPRINGS.sheet}
    >
      <Toast icon={icon}>{message}</Toast>
    </m.div>
  );
}

/**
 * Fixed stack of live toasts. Rendered once, near the root.
 * `output` rather than a `div[role=status]` — Biome's useSemanticElements
 * rejects the latter, and `output` already carries the live-region semantics.
 */
export function ToastViewport() {
  const toasts = useToastStore((s) => s.toasts);

  /*
    빈 배열에서도 뷰포트를 계속 렌더해요. 예전에는 `null` 로 빠졌는데, 그러면
    마지막 토스트가 사라질 때 부모가 같이 언마운트돼서 `AnimatePresence` 가
    나가는 애니메이션을 재생할 대상을 잃습니다 — 마지막 하나만 툭 사라졌어요.

    `pointer-events-none` 이라 빈 상태의 이 요소는 클릭을 막지 않습니다.
  */
  return (
    <output
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex flex-col items-center gap-2 px-4"
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastRow key={t.id} {...t} />
        ))}
      </AnimatePresence>
    </output>
  );
}
